import { createContext, useContext, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const NotificationContext = createContext();

const NotificationBar = ({ notifications = [] }) => {
    return (
        <AnimatePresence>
            {notifications.map((notification) => (
                <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className={`fixed top-20 left-0 right-0 mx-auto w-full max-w-2xl z-[9999] px-4`}
                >
                    <div
                        className={`rounded-lg shadow-lg p-4 flex items-start gap-3 border-l-4 ${notification.status
                                ? "bg-green-50 border-green-500"
                                : "bg-red-50 border-red-500"
                            }`}
                    >
                        <div className="mt-1">
                            {notification.status ? (
                                <svg
                                    className="w-5 h-5 text-green-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            ) : (
                                <svg
                                    className="w-5 h-5 text-red-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            )}
                        </div>

                        <div className="flex-1">
                            <p
                                className={`font-semibold text-sm ${notification.status ? "text-green-800" : "text-red-800"
                                    }`}
                                style={{ fontFamily: "Satoshi, sans-serif" }}
                            >
                                {notification.status ? "Success" : "Error"}
                            </p>
                            <p
                                className={`text-sm mt-1 ${notification.status ? "text-green-700" : "text-red-700"
                                    }`}
                                style={{ fontFamily: "Satoshi, sans-serif" }}
                            >
                                {notification.message}
                            </p>
                        </div>

                        <button
                            onClick={() => notification.onClose(notification.id)}
                            className={`text-gray-400 hover:text-gray-600 flex-shrink-0 mt-1`}
                        >
                            <X size={18} />
                        </button>
                    </div>
                </motion.div>
            ))}
        </AnimatePresence>
    );
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);

    const showNotification = useCallback(
        (message, status = true, duration = 5000) => {
            const id = Date.now();
            const newNotification = {
                id,
                message,
                status,
                onClose: (notificationId) => {
                    setNotifications((prev) =>
                        prev.filter((notif) => notif.id !== notificationId),
                    );
                },
            };

            setNotifications((prev) => [...prev, newNotification]);

            if (duration > 0) {
                const timer = setTimeout(() => {
                    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
                }, duration);

                return () => clearTimeout(timer);
            }
        },
        [],
    );

    return (
        <NotificationContext.Provider value={{ showNotification, notifications }}>
            <NotificationBar notifications={notifications} />
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error(
            "useNotification must be used within NotificationProvider",
        );
    }
    return context;
};
